import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { GuestOnlyGuard } from '@/components/auth-guard';
import { LoginForm } from '@/features/auth/components/login-form';
import { AuthCard } from '@/features/auth/views';
import { resolveAuthRedirectTarget } from '@/lib/auth-redirect';

const FOOTER_LINK_STYLE = {
  color: 'var(--vq-accent)',
  textDecoration: 'none',
} as const;
const STRONG_LINK_STYLE = {
  color: 'var(--vq-ink)',
  textDecoration: 'none',
  fontWeight: 500,
} as const;

interface LoginSearch {
  /** Path to navigate to after a successful sign-in. Open redirect
   *  protection: only an in-app same-origin path is honoured (must
   *  start with `/`, must not start with `//` or contain a scheme). */
  next?: string;
}

function LoginRoute(): ReactNode {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const search = Route.useSearch();
  const authenticatedRedirect = resolveAuthRedirectTarget(search.next);

  return (
    <GuestOnlyGuard redirectTo={authenticatedRedirect}>
      <AuthCard
        title={t('login.title')}
        subtitle={t('login.subtitle')}
        footer={
          <div className="flex flex-col gap-2">
            <Link to="/reset-password" style={FOOTER_LINK_STYLE} className="hover:underline">
              {t('login.forgotPassword')}
            </Link>
            <span>
              {t('login.noAccount')}{' '}
              <Link to="/register" style={STRONG_LINK_STYLE} className="hover:underline">
                {t('login.createOne')}
              </Link>
            </span>
          </div>
        }
      >
        <LoginForm
          onSuccess={() => {
            // Bounce back to the originating page when ?next is set, but
            // only when it points to a same-origin in-app path. Anything
            // else falls through to /dashboard so a malicious ?next= can't
            // redirect users to an external phishing target.
            //
            // The legacy invite-accept flow stashes the return path in
            // sessionStorage; consume it as a fallback so older link
            // formats keep working.
            let target = authenticatedRedirect;
            if (target === '/dashboard') {
              try {
                const stashed = window.sessionStorage.getItem('invite:returnTo');
                target = resolveAuthRedirectTarget(stashed ?? undefined);
                if (target !== '/dashboard') {
                  window.sessionStorage.removeItem('invite:returnTo');
                }
              } catch {
                // sessionStorage may throw in privacy modes — non-fatal.
              }
            }
            void navigate({ to: target });
          }}
        />
      </AuthCard>
    </GuestOnlyGuard>
  );
}

export const Route = createFileRoute('/login')({
  component: LoginRoute,
  validateSearch: (raw: Record<string, unknown>): LoginSearch => {
    const next = typeof raw.next === 'string' ? raw.next : undefined;
    return next !== undefined ? { next } : {};
  },
});
